import { FileSpreadsheet, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

interface FileUploadAreaProps {
  onFileSelected: (file: File) => void;
  selectedFile: File | null;
  onClear: () => void;
  disabled?: boolean;
}

export function FileUploadArea({ onFileSelected, selectedFile, onClear, disabled }: FileUploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) validateAndSelect(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    e.target.value = '';
  }

  function validateAndSelect(file: File) {
    const allowed = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!allowed.includes(file.type) && !['csv', 'xlsx', 'xls'].includes(ext ?? '')) {
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      return;
    }
    onFileSelected(file);
  }

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (selectedFile) {
    return (
      <div className="flex items-center gap-3 p-4 border-2 border-blue-200 bg-blue-50 rounded-xl">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-theme truncate">{selectedFile.name}</p>
          <p className="text-xs text-theme-secondary">{formatSize(selectedFile.size)}</p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onClear}
            className="p-1 rounded-lg hover:bg-blue-100 text-theme-secondary hover:text-theme transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`
        border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
        ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="p-3 bg-slate-100 rounded-full">
          <Upload className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-theme">
          Arrastra tu archivo aquí o <span className="text-blue-600">selecciona</span>
        </p>
        <p className="text-xs text-theme-secondary">CSV, XLSX, XLS · Máximo 10 MB</p>
      </div>
    </div>
  );
}
